import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { TimelogService } from 'src/app/services/timelog.service';
import { SharedService } from 'src/app/services/shared.service';

type ExportFormat = 'xlsx' | 'pdf';
type Period = 'current' | 'month' | 'range';
type Billable = 'both' | 'billable' | 'non-billable';

/**
 * The timesheet export: an "Export ▾" button, its PDF / Excel menu, and the
 * filter popup that builds the file on the server and downloads it.
 *
 * The page passes in what it already shows (the month, the member picked in
 * the chart, the member list) so the popup opens prefilled. Developer, Super
 * Admin and Admin can pick anyone; everyone else is locked to themselves, and
 * the server enforces the same rule.
 *
 * With a projectId (the Project Details time log sidebar) the export covers
 * that project only and the file is named after it.
 */
@Component({
  selector: 'app-timesheet-export',
  templateUrl: './timesheet-export.component.html',
  styleUrls: ['./timesheet-export.component.scss'],
  standalone: false,
})
export class TimesheetExportComponent implements OnChanges, OnDestroy {
  /** The month the page is showing. */
  @Input() month: Date | null = null;
  /** The member picked in the page's chart, if any. */
  @Input() memberId: string | null = null;
  /** People to choose from: { _id, label | fullName | firstName/lastName, email? }. */
  @Input() users: any[] = [];

  /** Limit the export to one project. */
  @Input() projectId: string | null = null;
  @Input() projectName: string | null = null;
  /** Members already filtered on the page; prefills Members. */
  @Input() initialMemberIds: string[] | null = null;
  /** Date range already filtered on the page; prefills Date range. */
  @Input() initialRange: Date[] | null = null;
  /** Raise above whatever the button sits in (the project sidebar is 66666). */
  @Input() baseZIndex = 10000;
  /** Small icon-only trigger ("Export this view"), e.g. in the chart bar. */
  @Input() compact = false;

  /** `users` with a `label` on each, which the member picker shows. */
  userOptions: any[] = [];

  menuOpen = false;
  dialogOpen = false;
  exporting = false;

  format: ExportFormat = 'xlsx';
  period: Period = 'current';
  pickedMonth: Date = new Date();
  range: Date[] | null = null;
  billable: Billable = 'both';
  memberIds: string[] = [];

  /** Projects filter (Time Logs page only; the project sidebar is fixed to its project). */
  projectIds: string[] = [];
  projectOptions: { _id: string; name: string }[] = [];
  private projectsLoaded = false;
  /** Members of the chosen projects (assigned, or logged time in the period); null when no project is chosen. */
  private projectMembers: any[] | null = null;
  membersLoading = false;
  /** Shown under Members when picked members were dropped after a project change. */
  memberNote = '';
  private membersRequest = 0;
  private scopeTimer: any = null;

  /** Time Log Type choices; `tone` colours the dot (green / orange / both). */
  readonly billableOptions = [
    { label: 'Both', value: 'both', hint: 'Billable + Non-billable hours', tone: 'both' },
    { label: 'Billable only', value: 'billable', hint: 'Only billable hours', tone: 'b' },
    { label: 'Non-billable only', value: 'non-billable', hint: 'Only non-billable hours', tone: 'n' },
  ];

  readonly formatOptions: { value: ExportFormat; badge: string; label: string; hint: string }[] = [
    { value: 'pdf', badge: 'PDF', label: 'PDF', hint: 'Printable timesheet' },
    { value: 'xlsx', badge: 'XLS', label: 'Excel', hint: 'Editable .xlsx sheet' },
  ];

  /** Developer(1) / Super Admin(2) / Admin(3). */
  readonly privileged: boolean;
  private readonly self: { _id: string; label: string } | null;

  constructor(
    private host: ElementRef<HTMLElement>,
    private timelogService: TimelogService,
    private sharedService: SharedService,
    private toastr: ToastrService,
  ) {
    this.privileged = [1, 2, 3].includes(Number(this.sharedService.getRoleNumber()));
    this.self = TimesheetExportComponent.readSelf();
  }

  private static readSelf(): { _id: string; label: string } | null {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      if (!u?._id) return null;
      const label = u.fullName || [u.firstName, u.lastName].filter(Boolean).join(' ').trim() || u.email || 'Me';
      return { _id: String(u._id), label };
    } catch {
      return null;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['users']) {
      this.userOptions = (this.users || [])
        .filter((u) => u && u._id)
        .map((u) => ({
          ...u,
          _id: String(u._id),
          label:
            u.label ||
            u.fullName ||
            [u.firstName, u.lastName].filter(Boolean).join(' ').trim() ||
            u.email ||
            'Unknown member',
        }));
    }
  }

  /* ── menu ────────────────────────────────────────────────────────────── */

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.menuOpen && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.menuOpen = false;
  }

  /* ── popup ───────────────────────────────────────────────────────────── */

  /** Opens the popup prefilled from what the page is showing. */
  choose(format: ExportFormat): void {
    this.menuOpen = false;
    this.format = format;

    const shown = this.month ? new Date(this.month) : new Date();
    const today = new Date();
    const isThisMonth = shown.getFullYear() === today.getFullYear() && shown.getMonth() === today.getMonth();
    this.period = isThisMonth ? 'current' : 'month';
    this.pickedMonth = new Date(shown.getFullYear(), shown.getMonth(), 1);
    this.range = [
      new Date(shown.getFullYear(), shown.getMonth(), 1),
      new Date(shown.getFullYear(), shown.getMonth() + 1, 0),
    ];

    // A date range already chosen on the page wins over the month.
    const [from, to] = this.initialRange || [];
    if (from) {
      this.period = 'range';
      this.range = [new Date(from), new Date(to || from)];
    }

    this.billable = 'both';
    if (this.privileged) {
      if (this.initialMemberIds?.length) {
        this.memberIds = this.initialMemberIds.map(String);
      } else {
        this.memberIds = this.memberId ? [String(this.memberId)] : [];
      }
    } else {
      this.memberIds = this.self ? [this.self._id] : [];
    }
    this.projectIds = [];
    this.projectMembers = null;
    this.memberNote = '';
    if (this.showProjects) this.loadProjects();
    this.dialogOpen = true;
  }

  setPeriod(p: Period): void {
    this.period = p;
    this.onScopeChange();
  }

  /* ── projects filter ─────────────────────────────────────────────────── */

  /** Only on the Time Logs page; the project sidebar export is fixed to its project. */
  get showProjects(): boolean {
    return !this.projectId;
  }

  private loadProjects(): void {
    if (this.projectsLoaded) return;
    this.timelogService.getExportProjects().subscribe({
      next: (res: any) => {
        this.projectOptions = (res?.data || []).map((p: any) => ({ _id: String(p._id), name: p.name }));
        this.projectsLoaded = true;
      },
      error: () => this.toastr.error('Could not load projects.'),
    });
  }

  /**
   * The projects or the period changed: reload who logged time on the chosen
   * projects. Debounced, and run after the pickers have written their value.
   */
  onScopeChange(): void {
    if (!this.showProjects) return;
    clearTimeout(this.scopeTimer);
    this.scopeTimer = setTimeout(() => this.loadProjectMembers(), 250);
  }

  private loadProjectMembers(): void {
    const d = this.dates;
    if (!this.projectIds.length || !d) {
      this.membersRequest++; // ignore any reply still on its way
      this.projectMembers = null;
      this.membersLoading = false;
      this.memberNote = '';
      return;
    }
    if (!this.privileged) return; // locked to themselves either way
    const request = ++this.membersRequest;
    this.membersLoading = true;
    this.timelogService
      .getExportMembers({
        projectIds: this.projectIds,
        startDate: this.ymd(d.start),
        endDate: this.ymd(d.end),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })
      .subscribe({
        next: (res: any) => {
          if (request !== this.membersRequest) return; // a newer request is on its way
          this.membersLoading = false;
          this.projectMembers = (res?.data || []).map((u: any) => ({ ...u, _id: String(u._id) }));
          // Drop picked members with no logs on these projects.
          const keep = new Set(this.projectMembers!.map((u) => u._id));
          const dropped = this.memberIds.filter((id) => !keep.has(String(id)));
          if (dropped.length) {
            this.memberIds = this.memberIds.filter((id) => keep.has(String(id)));
            this.memberNote = `${dropped.length} member${dropped.length > 1 ? 's' : ''} removed: not on the chosen projects.`;
          } else {
            this.memberNote = '';
          }
        },
        error: () => {
          if (request !== this.membersRequest) return;
          this.membersLoading = false;
          this.projectMembers = null;
          this.toastr.error('Could not load members for the chosen projects.');
        },
      });
  }

  get projectsSummary(): string {
    if (!this.projectIds.length) return 'All projects';
    const names = this.projectIds.map((id) => this.projectOptions.find((p) => p._id === String(id))?.name || 'Project');
    return names.join(', ');
  }

  /** Members hint while a project filter is on. */
  get projectMembersHint(): string {
    if (this.membersLoading) return 'Loading members of the chosen projects…';
    const n = this.projectMembers?.length ?? 0;
    const what = this.projectIds.length > 1 ? 'the chosen projects' : 'the chosen project';
    if (!n) return `No members on ${what}.`;
    return `Showing ${n} member${n > 1 ? 's' : ''} of ${what} (assigned, or logged time in this period). Leave empty to export all of them.`;
  }

  /** The popup opens on the format picked from the menu; it can be switched here. */
  setFormat(f: ExportFormat): void {
    if (!this.exporting) this.format = f;
  }

  get billableOption() {
    return this.billableOptions.find((o) => o.value === this.billable) || this.billableOptions[0];
  }

  /* ── summary panel ───────────────────────────────────────────────────── */

  get periodSummary(): string {
    const d = this.dates;
    return d ? `${this.dmy(d.start)} to ${this.dmy(d.end)}` : 'Pick the dates';
  }

  get membersSummary(): string {
    if (!this.privileged) return this.self?.label || 'Me';
    if (!this.memberIds.length) return this.allMembersLabel;
    const names = this.memberIds.map(
      (id) => this.userOptions.find((u) => String(u._id) === String(id))?.label || 'Unknown member',
    );
    return names.length <= 3 ? names.join(', ') : `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
  }

  get memberOptions(): any[] {
    if (this.privileged) {
      // With a project filter, only people who logged time on those projects.
      if (this.showProjects && this.projectIds.length && this.projectMembers) return this.projectMembers;
      return this.userOptions;
    }
    return this.self ? [this.self] : [];
  }

  get allMembersLabel(): string {
    if (this.projectId) return 'All project members';
    return this.projectIds.length ? 'All members of the chosen projects' : 'All members';
  }

  get formatLabel(): string {
    return this.format === 'pdf' ? 'PDF' : 'Excel';
  }

  /** The first and last day to export, or null while a range is half picked. */
  get dates(): { start: Date; end: Date } | null {
    const monthBounds = (d: Date) => ({
      start: new Date(d.getFullYear(), d.getMonth(), 1),
      end: new Date(d.getFullYear(), d.getMonth() + 1, 0),
    });
    if (this.period === 'current') return monthBounds(new Date());
    if (this.period === 'month') return this.pickedMonth ? monthBounds(this.pickedMonth) : null;
    const [start, end] = this.range || [];
    if (!start) return null;
    return { start, end: end || start };
  }

  get canExport(): boolean {
    return !this.exporting && !!this.dates;
  }

  /** Same name the server gives the file. */
  get fileName(): string {
    const d = this.dates;
    if (!d) return '';
    let who = 'All Members';
    if (this.projectId) {
      who = this.projectName || 'Project';
    } else if (this.projectIds.length === 1) {
      who = this.projectOptions.find((p) => p._id === String(this.projectIds[0]))?.name || 'Project';
    } else if (this.projectIds.length > 1) {
      who = 'Selected Projects';
    } else if (!this.privileged) {
      who = this.self?.label || 'Me';
    } else if (this.memberIds.length === 1) {
      const u = this.userOptions.find((x) => String(x._id) === String(this.memberIds[0]));
      who = u?.label || 'Selected Members';
    } else if (this.memberIds.length > 1) {
      who = 'Selected Members';
    }
    who = who.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim() || 'Members';
    return `Timesheet-${who}-${this.dmy(d.start)} to ${this.dmy(d.end)}.${this.format}`;
  }

  get periodHint(): string {
    if (this.period === 'range') {
      return 'Each month gets its own rows. A month the range fully covers shows its name; a partly covered month shows its dates.';
    }
    return 'The Date column shows the month name.';
  }

  closeDialog(): void {
    if (this.exporting) return;
    this.dialogOpen = false;
  }

  /**
   * Inside the project sidebar the popup's backdrop stacks above the toast
   * container, so "No time logs found…" showed dimmed behind it. While the
   * popup is open, lift toasts above it (see .tse-toasts-on-top in the scss).
   */
  raiseToasts(on: boolean): void {
    document.body.classList.toggle('tse-toasts-on-top', on);
  }

  ngOnDestroy(): void {
    this.raiseToasts(false);
    clearTimeout(this.scopeTimer);
  }

  export(): void {
    const d = this.dates;
    if (!d || this.exporting) return;

    this.exporting = true;
    const payload = {
      format: this.format,
      startDate: this.ymd(d.start),
      endDate: this.ymd(d.end),
      billable: this.billable,
      memberIds: this.privileged ? this.memberIds : [],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      ...(this.projectId ? { projectId: String(this.projectId) } : {}),
      ...(!this.projectId && this.projectIds.length ? { projectIds: this.projectIds.map(String) } : {}),
    };

    this.timelogService.exportTimesheet(payload).subscribe({
      next: (res: any) => {
        this.exporting = false;
        const file = res?.data;
        if (!file?.fileUrl) {
          this.toastr.error('Could not create the timesheet. Please try again.');
          return;
        }
        this.download(file.fileUrl);
        this.dialogOpen = false;
        this.toastr.success(file.fileName, 'Timesheet downloaded');
      },
      error: (err) => {
        this.exporting = false;
        const message = err?.error?.message || 'Could not create the timesheet. Please try again.';
        if (err?.status === 404) this.toastr.warning(message);
        else this.toastr.error(message);
      },
    });
  }

  /**
   * The file is stored with "Content-Disposition: attachment", so following
   * the link saves it under its name and the page stays where it is.
   */
  private download(url: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    a.style.display = 'none';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  private pad(n: number): string {
    return String(n).padStart(2, '0');
  }

  private ymd(d: Date): string {
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`;
  }

  private dmy(d: Date): string {
    return `${this.pad(d.getDate())}-${this.pad(d.getMonth() + 1)}-${d.getFullYear()}`;
  }
}
